import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Clock,
  Eye,
  Shield,
  Zap,
} from 'lucide-react';

interface ProblemSolutionToggle {
  isShowingSolution: boolean;
  onToggle: (showSolution: boolean) => void;
}

export function InteractiveGovernanceComparison({
  isShowingSolution,
  onToggle,
}: ProblemSolutionToggle) {
  const problemData = {
    title: 'Traditional HOA Governance',
    color: 'from-red-100 to-red-200',
    textColor: 'text-red-800',
    borderColor: 'border-red-300',
    issues: [
      { icon: AlertTriangle, text: 'Board meetings drag for hours', severity: 'high' },
      { icon: Clock, text: 'Decisions take months to approve', severity: 'high' },
      { icon: Users, text: 'Politics and favoritism rule', severity: 'medium' },
      { icon: AlertTriangle, text: 'No transparency in decision-making', severity: 'high' },
      { icon: Clock, text: 'Disputes create community divisions', severity: 'medium' },
    ],
    stats: {
      decisionTime: '5+ months',
      transparency: 'Limited',
      satisfaction: '40%',
      disputes: 'Frequent',
    },
  };

  const solutionData = {
    title: 'Digital Community Governance',
    color: 'from-green-100 to-green-200',
    textColor: 'text-green-800',
    borderColor: 'border-green-300',
    benefits: [
      { icon: CheckCircle, text: 'Small, focused leadership teams (3-5 people)', impact: 'high' },
      { icon: Zap, text: 'Decisions made in days, not months', impact: 'high' },
      { icon: Eye, text: 'Everything visible to all residents', impact: 'medium' },
      { icon: Shield, text: 'Fair, transparent leader selection', impact: 'high' },
      { icon: CheckCircle, text: 'Built-in conflict resolution', impact: 'medium' },
    ],
    stats: {
      decisionTime: '3-7 days',
      transparency: 'Complete',
      satisfaction: '94%',
      disputes: 'Resolved Fast',
    },
  };

  const currentData = isShowingSolution ? solutionData : problemData;

  return (
    <Card
      className={`overflow-hidden transition-all duration-500 bg-linear-to-br ${currentData.color} border-2 ${currentData.borderColor}`}
    >
      <CardContent className="p-8">
        {/* Header with Toggle */}
        <div className="flex items-center justify-between mb-6">
          <h3 className={`text-xl font-bold ${currentData.textColor}`}>{currentData.title}</h3>

          <div className="flex gap-2">
            <Button
              size="sm"
              variant={!isShowingSolution ? 'default' : 'outline'}
              onClick={() => onToggle(false)}
              className={!isShowingSolution ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              Problem
            </Button>
            <Button
              size="sm"
              variant={isShowingSolution ? 'default' : 'outline'}
              onClick={() => onToggle(true)}
              className={isShowingSolution ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              Solution
            </Button>
          </div>
        </div>

        {/* Issues/Benefits List */}
        <div className="space-y-3 mb-6">
          {isShowingSolution
            ? solutionData.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-white/60 rounded-lg border border-green-200"
                >
                  <benefit.icon className="w-5 h-5 text-green-600 shrink-0" />
                  <span className="text-sm font-medium text-green-800">{benefit.text}</span>
                  {benefit.impact === 'high' && (
                    <Badge className="bg-green-200 text-green-800 text-xs">High Impact</Badge>
                  )}
                </div>
              ))
            : problemData.issues.map((issue, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-white/60 rounded-lg border border-red-200"
                >
                  <issue.icon className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-sm font-medium text-red-800">{issue.text}</span>
                  {issue.severity === 'high' && (
                    <Badge className="bg-red-200 text-red-800 text-xs">Critical</Badge>
                  )}
                </div>
              ))}
        </div>

        {/* Stats Comparison */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-3 bg-white/60 rounded-lg">
            <div className={`text-lg font-bold ${currentData.textColor}`}>
              {currentData.stats.decisionTime}
            </div>
            <div className="text-xs text-muted-foreground">Decision Time</div>
          </div>

          <div className="text-center p-3 bg-white/60 rounded-lg">
            <div className={`text-lg font-bold ${currentData.textColor}`}>
              {currentData.stats.transparency}
            </div>
            <div className="text-xs text-muted-foreground">Transparency</div>
          </div>

          <div className="text-center p-3 bg-white/60 rounded-lg">
            <div className={`text-lg font-bold ${currentData.textColor}`}>
              {currentData.stats.satisfaction}
            </div>
            <div className="text-xs text-muted-foreground">Satisfaction</div>
          </div>

          <div className="text-center p-3 bg-white/60 rounded-lg">
            <div className={`text-lg font-bold ${currentData.textColor}`}>
              {currentData.stats.disputes}
            </div>
            <div className="text-xs text-muted-foreground">Disputes</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          {isShowingSolution ? (
            <Button className="bg-green-600 hover:bg-green-700 text-white">
              Implement This System
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => onToggle(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              See the Solution
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function CompactHeroSection({
  onDemoRequest,
  onCalculatorOpen,
}: {
  onDemoRequest?: () => void;
  onCalculatorOpen?: () => void;
}) {
  const [showSolution, setShowSolution] = useState(false);

  return (
    <div className="bg-linear-to-br from-slate-50 to-blue-50 py-16">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Side */}
          <div className="space-y-8">
            <div className="space-y-4">
              <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                Governance Revolution
              </Badge>

              <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
                <span className="text-slate-900">Stop the Politics</span>
                <br />
                <span className="bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Start Getting Results
                </span>
              </h1>

              <p className="text-xl text-slate-600 leading-relaxed">
                A governance system designed to work even when people disagree. See how the subpanel
                structure ends the drama and gets decisions made.
              </p>
            </div>

            {/* Quick Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-xs border">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-sm">10x Faster</div>
                  <div className="text-xs text-muted-foreground">Decision Making</div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-xs border">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-semibold text-sm">100% Transparent</div>
                  <div className="text-xs text-muted-foreground">All Decisions</div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-xs border">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-sm">Dispute Proof</div>
                  <div className="text-xs text-muted-foreground">Built-in Resolution</div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-xs border">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <div className="font-semibold text-sm">30-90 Days</div>
                  <div className="text-xs text-muted-foreground">Setup Time</div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-linear-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 flex-1"
                onClick={onDemoRequest}
              >
                See How This Works for 406 Communities
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button size="lg" variant="outline" className="flex-1" onClick={onCalculatorOpen}>
                Calculate Your Savings
              </Button>
            </div>
          </div>

          {/* Interactive Comparison */}
          <div>
            <InteractiveGovernanceComparison
              isShowingSolution={showSolution}
              onToggle={setShowSolution}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
